import React, { useState, Children } from 'react';
import { ChevronLeft, Shield, Star, ChevronDown, ChevronUp } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { SegmentedControl } from '../components/SegmentedControl';
import { Rating } from '../components/Rating';
import { Badge } from '../components/Badge';
interface OwnerProfileProps {
  ownerId: string;
  onBack: () => void;
  onItemClick: (id: string) => void;
}
export function OwnerProfile({
  ownerId,
  onBack,
  onItemClick
}: OwnerProfileProps) {
  const [selectedTab, setSelectedTab] = useState('Listings');
  const [expandedReviews, setExpandedReviews] = useState<Set<string>>(new Set());
  const mockOwner = {
    name: 'Oliver M.',
    avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=200&q=80',
    bio: 'Sharing tools and gear with my neighbors. Happy to help with any questions!',
    totalBookings: 180,
    verified: true,
    ratings: {
      asBorrower: {
        rating: 4.9,
        count: 32
      },
      asLender: {
        rating: 4.8,
        count: 28
      }
    }
  };
  const mockListings = [{
    id: '1',
    image: 'https://images.unsplash.com/photo-1504148455328-c376907d081c?w=800&q=80',
    title: 'Wireless Power Drill',
    price: 15,
    rating: 4.8,
    reviewCount: 47
  }, {
    id: '2',
    image: 'https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=800&q=80',
    title: 'Camping Tent (6-person)',
    price: 35,
    rating: 4.7,
    reviewCount: 28
  }, {
    id: '3',
    image: 'https://images.unsplash.com/photo-1519340241574-2cec6aef0c01?w=800&q=80',
    title: 'Baby Stroller',
    price: 20,
    rating: 4.9,
    reviewCount: 32
  }, {
    id: '4',
    image: 'https://images.unsplash.com/photo-1505236858219-8359eb29e329?w=800&q=80',
    title: 'Folding Tables Set',
    price: 40,
    rating: 4.6,
    reviewCount: 15
  }];
  const mockReviews = [{
    id: '1',
    type: 'borrowed',
    reviewer: {
      name: 'Hellen H.',
      avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=200&q=80'
    },
    item: {
      title: "Oliver's power tool",
      image: 'https://images.unsplash.com/photo-1504148455328-c376907d081c?w=200&q=80'
    },
    rating: 5,
    text: "Oliver's power tool was great, got the job done to hang my curtain rods! Would definitely rent again for future projects.",
    date: '2 weeks ago'
  }, {
    id: '2',
    type: 'lent',
    reviewer: {
      name: 'James C.',
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&q=80'
    },
    item: {
      title: 'Inflatable slide',
      image: 'https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=200&q=80'
    },
    rating: 5,
    text: 'Oliver returned my inflatable slide folded up and rinsed and was timely to communicate. Great renter, would recommend!',
    date: '1 month ago'
  }, {
    id: '3',
    type: 'borrowed',
    reviewer: {
      name: 'Sarah M.',
      avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=200&q=80'
    },
    item: {
      title: 'Camping gear',
      image: 'https://images.unsplash.com/photo-1504280390367-361c6d9f38f4?w=200&q=80'
    },
    rating: 5,
    text: 'Perfect camping gear for our weekend trip. Oliver was very helpful with setup instructions and everything was in excellent condition.',
    date: '1 month ago'
  }];
  const toggleReviewExpansion = (reviewId: string) => {
    setExpandedReviews(prev => {
      const newSet = new Set(prev);
      if (newSet.has(reviewId)) {
        newSet.delete(reviewId);
      } else {
        newSet.add(reviewId);
      }
      return newSet;
    });
  };
  const container = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08
      }
    }
  };
  const item = {
    hidden: {
      opacity: 0,
      y: 20
    },
    show: {
      opacity: 1,
      y: 0
    }
  };
  return <div className="min-h-screen bg-cream pb-24" data-id="element-1324">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-white/95 backdrop-blur-sm border-b border-sand/50" data-id="element-1325">
        <div className="max-w-md mx-auto px-4 py-3" data-id="element-1326">
          <button onClick={onBack} className="p-2 -ml-2 hover:bg-sand/20 rounded-full transition-colors" data-id="element-1327">
            <ChevronLeft size={24} className="text-charcoal" data-id="element-1328" />
          </button>
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 py-6 space-y-6" data-id="element-1329">
        {/* Profile Card */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="bg-white rounded-ios-lg p-6 shadow-sm border border-sand/50" data-id="element-1330">
          <div className="flex items-start gap-4 mb-4" data-id="element-1331">
            <div className="relative" data-id="element-1332">
              <img src={mockOwner.avatar} alt={mockOwner.name} className="w-20 h-20 rounded-full object-cover border-2 border-white shadow-sm" data-id="element-1333" />
              {mockOwner.verified && <div className="absolute -bottom-1 -right-1 w-8 h-8 bg-dark-green rounded-full flex items-center justify-center border-2 border-white shadow-md" data-id="element-1334">
                  <Shield size={16} className="text-white" data-id="element-1335" />
                </div>}
            </div>
            <div className="flex-1" data-id="element-1336">
              <h1 className="text-xl font-bold text-charcoal mb-1" data-id="element-1337">
                {mockOwner.name}
              </h1>
              <p className="text-sm text-moss-gray mb-2" data-id="element-1338">
                {mockOwner.totalBookings} total bookings
              </p>
            </div>
          </div>

          <p className="text-sm text-charcoal/80 leading-relaxed mb-4" data-id="element-1339">
            {mockOwner.bio}
          </p>

          {/* Ratings */}
          <div className="grid grid-cols-2 gap-3 pt-4 border-t border-sand/50" data-id="element-1340">
            <div className="flex items-center gap-2" data-id="element-1341">
              <Star size={18} className="fill-clay text-clay" data-id="element-1342" />
              <div data-id="element-1343">
                <div className="text-lg font-bold text-charcoal leading-none mb-1" data-id="element-1344">
                  {mockOwner.ratings.asBorrower.rating}
                </div>
                <div className="text-xs text-moss-gray" data-id="element-1345">
                  ({mockOwner.ratings.asBorrower.count}) as borrower
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2" data-id="element-1346">
              <Star size={18} className="fill-clay text-clay" data-id="element-1347" />
              <div data-id="element-1348">
                <div className="text-lg font-bold text-charcoal leading-none mb-1" data-id="element-1349">
                  {mockOwner.ratings.asLender.rating}
                </div>
                <div className="text-xs text-moss-gray" data-id="element-1350">
                  ({mockOwner.ratings.asLender.count}) as lender
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Segmented Control */}
        <SegmentedControl options={['Listings', 'Reviews']} selected={selectedTab} onChange={setSelectedTab} data-id="element-1351" />

        {/* Content */}
        <AnimatePresence mode="wait" data-id="element-1352">
          {selectedTab === 'Listings' && <motion.div key="listings" variants={container} initial="hidden" animate="show" exit="hidden" className="grid grid-cols-2 gap-3" data-id="element-1353">
              {mockListings.map(listing => <motion.button key={listing.id} variants={item} whileTap={{
            scale: 0.98
          }} onClick={() => onItemClick(listing.id)} className="bg-white rounded-ios-lg overflow-hidden shadow-sm border border-sand/50 hover:border-dark-green/30 transition-all active:shadow-none text-left" data-id="element-1354">
                  <div className="aspect-square" data-id="element-1355">
                    <img src={listing.image} alt={listing.title} className="w-full h-full object-cover" data-id="element-1356" />
                  </div>
                  <div className="p-3" data-id="element-1357">
                    <h3 className="font-semibold text-sm text-charcoal mb-2 line-clamp-2 leading-tight" data-id="element-1358">
                      {listing.title}
                    </h3>
                    <div className="flex items-center justify-between" data-id="element-1359">
                      <span className="text-dark-green font-bold text-base" data-id="element-1360">
                        ${listing.price}
                      </span>
                      <div className="flex items-center gap-1" data-id="element-1361">
                        <Star size={12} className="fill-clay text-clay" data-id="element-1362" />
                        <span className="text-xs font-medium text-charcoal" data-id="element-1363">
                          {listing.rating}
                        </span>
                        <span className="text-xs text-moss-gray" data-id="element-1364">
                          ({listing.reviewCount})
                        </span>
                      </div>
                    </div>
                  </div>
                </motion.button>)}
            </motion.div>}

          {selectedTab === 'Reviews' && <motion.div key="reviews" variants={container} initial="hidden" animate="show" exit="hidden" className="space-y-3" data-id="element-1365">
              {mockReviews.map(review => {
            const isExpanded = expandedReviews.has(review.id);
            const shortText = review.text.slice(0, 100) + '...';
            const shouldTruncate = review.text.length > 100;
            return <motion.div key={review.id} variants={item} className="bg-white rounded-ios-lg p-4 shadow-sm border border-sand/50" data-id="element-1366">
                    <div className="flex items-start gap-3 mb-3" data-id="element-1367">
                      <img src={review.reviewer.avatar} alt={review.reviewer.name} className="w-12 h-12 rounded-full object-cover border-2 border-white shadow-sm" data-id="element-1368" />
                      <div className="flex-1 min-w-0" data-id="element-1369">
                        <div className="flex items-center justify-between mb-1" data-id="element-1370">
                          <span className="font-semibold text-charcoal" data-id="element-1371">
                            {review.reviewer.name}
                          </span>
                          <Rating value={review.rating} size={14} data-id="element-1372" />
                        </div>
                        <Badge variant={review.type === 'borrowed' ? 'success' : 'neutral'} size="sm" className={review.type === 'borrowed' ? 'bg-dark-green/10 text-dark-green border-dark-green/20' : 'bg-clay/10 text-clay border-clay/20'} data-id="element-1373">
                          {review.type === 'borrowed' ? 'Borrowed from Oliver' : 'Lent to Oliver'}
                        </Badge>
                      </div>
                    </div>

                    <div className="flex gap-3" data-id="element-1374">
                      <img src={review.item.image} alt={review.item.title} className="w-16 h-16 rounded-ios object-cover border border-sand/50" data-id="element-1375" />
                      <div className="flex-1 min-w-0" data-id="element-1376">
                        <AnimatePresence mode="wait" data-id="element-1377">
                          <motion.p key={isExpanded ? 'expanded' : 'collapsed'} initial={{
                      opacity: 0
                    }} animate={{
                      opacity: 1
                    }} exit={{
                      opacity: 0
                    }} transition={{
                      duration: 0.2
                    }} className="text-sm text-charcoal/80 leading-relaxed mb-2" data-id="element-1378">
                            {shouldTruncate && !isExpanded ? shortText : review.text}
                          </motion.p>
                        </AnimatePresence>
                        {shouldTruncate && <button onClick={() => toggleReviewExpansion(review.id)} className="text-xs font-medium text-dark-green hover:underline flex items-center gap-1" data-id="element-1379">
                            {isExpanded ? <>
                                See less <ChevronUp size={14} data-id="element-1380" />
                              </> : <>
                                See more <ChevronDown size={14} data-id="element-1381" />
                              </>}
                          </button>}
                        <span className="text-xs text-moss-gray mt-2 block" data-id="element-1382">
                          {review.date}
                        </span>
                      </div>
                    </div>
                  </motion.div>;
          })}
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
}